import React, { useState, useEffect } from 'react';
import { FocusedLayout } from '../../components/layout/FocusedLayout';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { TrendingUp, Clock, DollarSign, CheckCircle, Timer, ArrowDownToLine } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const MyGrowthPackagesPage = () => {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [withdrawing, setWithdrawing] = useState(null);
  const [withdrawAmount, setWithdrawAmount] = useState({});

  useEffect(() => {
    fetchMyPackages();
  }, []);

  const fetchMyPackages = async () => {
    try {
      const response = await fetch(`${API_URL}/api/user/growth-packages/my-packages`, {
        credentials: 'include'
      });
      const data = await response.json();
      setPackages(data.packages || []);
    } catch (error) {
      toast.error('Failed to load packages');
    } finally {
      setLoading(false);
    }
  };

  const handleWithdraw = async (packageId, maxAmount) => {
    const amount = parseFloat(withdrawAmount[packageId] || 0);
    
    if (amount <= 0 || amount > maxAmount) {
      toast.error(`Enter amount between 0 and ${maxAmount}`);
      return;
    }
    
    setWithdrawing(packageId);
    try {
      const response = await fetch(`${API_URL}/api/user/growth-packages/my-packages/${packageId}/withdraw`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ amount })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Withdrawal failed');
      }
      
      const data = await response.json();
      toast.success(`Withdrawn ${data.net_amount} ${data.currency} (fee: ${data.fee})`);
      setWithdrawAmount({...withdrawAmount, [packageId]: ''});
      fetchMyPackages();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setWithdrawing(null);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
      active: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
      completed: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
      cancelled: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
    };
    return colors[status] || colors.pending;
  };

  const formatTimeRemaining = (nextPayoutAt) => {
    const now = new Date();
    const next = new Date(nextPayoutAt);
    const diff = next - now;
    
    if (diff <= 0) return 'Processing...';
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    return `${hours}h ${minutes}m`;
  };

  return (
    <FocusedLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold">My Growth Packages</h1>
          <p className="text-muted-foreground">Track your daily earnings and withdraw anytime</p>
        </div>

        {loading ? (
          <div className="text-center py-12 text-muted-foreground">Loading...</div>
        ) : packages.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <TrendingUp className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground mb-4">You haven't purchased any growth packages yet</p>
              <Button onClick={() => window.location.href = '/user/growth-packages'}>Browse Packages</Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {packages.map((pkg) => (
              <Card key={pkg.id}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <CardTitle>{pkg.package_name}</CardTitle>
                      <Badge className={getStatusColor(pkg.status)}>
                        {pkg.status.toUpperCase()}
                      </Badge>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-muted-foreground">Purchased</p>
                      <p className="text-sm">{new Date(pkg.purchased_at).toLocaleDateString()}</p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                    <div className="p-4 bg-muted rounded-lg">
                      <p className="text-sm text-muted-foreground mb-1">Package Price</p>
                      <p className="text-2xl font-bold">{pkg.currency} {pkg.package_price}</p>
                    </div>

                    <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                      <p className="text-sm text-muted-foreground mb-1">Earned So Far</p>
                      <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                        {pkg.currency} {pkg.earned_amount.toFixed(2)}
                      </p>
                    </div>

                    <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                      <p className="text-sm text-muted-foreground mb-1">Withdrawable</p>
                      <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                        {pkg.currency} {pkg.withdrawable_balance.toFixed(2)}
                      </p>
                    </div>

                    <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                      <p className="text-sm text-muted-foreground mb-1">Expected Total</p>
                      <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                        {pkg.currency} {pkg.total_earnings_expected.toFixed(2)}
                      </p>
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium">Progress</span>
                      <span className="text-sm text-muted-foreground">
                        {pkg.days_completed} / {pkg.duration_days} Days
                      </span>
                    </div>
                    <div className="w-full bg-muted rounded-full h-3">
                      <div 
                        className="bg-gradient-to-r from-blue-500 to-purple-500 h-3 rounded-full transition-all"
                        style={{ width: `${pkg.progress_percentage}%` }}
                      />
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      {pkg.progress_percentage.toFixed(1)}% Complete
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                    <div className="flex items-center gap-2 p-3 bg-muted rounded-lg">
                      <DollarSign className="h-5 w-5 text-green-600" />
                      <div>
                        <p className="text-xs text-muted-foreground">Daily Payout</p>
                        <p className="font-bold">{pkg.currency} {pkg.daily_payout}</p>
                      </div>
                    </div>

                    {pkg.status === 'active' && (
                      <div className="flex items-center gap-2 p-3 bg-muted rounded-lg">
                        <Timer className="h-5 w-5 text-blue-600" />
                        <div>
                          <p className="text-xs text-muted-foreground">Next Payout In</p>
                          <p className="font-bold">{formatTimeRemaining(pkg.next_payout_at)}</p>
                        </div>
                      </div>
                    )}

                    <div className="flex items-center gap-2 p-3 bg-muted rounded-lg">
                      <Clock className="h-5 w-5 text-orange-600" />
                      <div>
                        <p className="text-xs text-muted-foreground">Days Remaining</p>
                        <p className="font-bold">{pkg.days_remaining}</p>
                      </div>
                    </div>
                  </div>

                  {/* Withdrawal Section - Always show if balance > 0 */}
                  {pkg.withdrawable_balance > 0 && (
                    <div className="mt-4 p-4 border-2 border-blue-200 dark:border-blue-800 rounded-lg bg-blue-50 dark:bg-blue-900/20">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="font-semibold text-blue-900 dark:text-blue-100">
                          💰 Ready to Withdraw
                        </h3>
                        <Badge variant="outline" className="bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300">
                          {pkg.currency} {pkg.withdrawable_balance.toFixed(2)} Available
                        </Badge>
                      </div>
                      
                      <div className="flex flex-col sm:flex-row items-start sm:items-end gap-3">
                        <div className="flex-1 w-full">
                          <label className="text-sm font-medium mb-2 block text-blue-900 dark:text-blue-100">
                            Enter Withdrawal Amount
                          </label>
                          <Input 
                            type="number"
                            step="0.01"
                            placeholder={`Max: ${pkg.withdrawable_balance.toFixed(2)}`}
                            value={withdrawAmount[pkg.id] || ''}
                            onChange={(e) => setWithdrawAmount({...withdrawAmount, [pkg.id]: e.target.value})}
                            className="h-11 bg-white dark:bg-slate-950"
                          />
                          <div className="flex items-center justify-between mt-1">
                            <p className="text-xs text-muted-foreground">
                              Fee: {pkg.withdrawal_fee_percentage}% • Min: {pkg.currency} {pkg.min_withdrawal_amount}
                            </p>
                            <Button
                              type="button"
                              variant="link"
                              size="sm"
                              className="h-auto p-0 text-xs"
                              onClick={() => setWithdrawAmount({...withdrawAmount, [pkg.id]: pkg.withdrawable_balance.toFixed(2)})}
                            >
                              Withdraw All
                            </Button>
                          </div>
                        </div>
                        <Button 
                          onClick={() => handleWithdraw(pkg.id, pkg.withdrawable_balance)}
                          disabled={withdrawing === pkg.id || !withdrawAmount[pkg.id] || parseFloat(withdrawAmount[pkg.id]) <= 0}
                          size="lg"
                          className="w-full sm:w-auto h-11 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                        >
                          <ArrowDownToLine className="h-4 w-4 mr-2" />
                          {withdrawing === pkg.id ? 'Processing...' : 'Withdraw Now'}
                        </Button>
                      </div>
                    </div>
                  )}
                  
                  {/* Show message if no withdrawable balance yet */}
                  {pkg.withdrawable_balance === 0 && pkg.status === 'active' && (
                    <div className="mt-4 p-4 border border-amber-200 dark:border-amber-800 rounded-lg bg-amber-50 dark:bg-amber-900/20">
                      <div className="flex items-center gap-2 text-amber-800 dark:text-amber-200">
                        <Clock className="h-5 w-5" />
                        <p className="text-sm font-medium">
                          Your first payout is being processed. Earnings will appear here soon!
                        </p>
                      </div>
                    </div>
                  )}

                  {pkg.status === 'completed' && (
                    <div className="flex items-center gap-2 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg mt-4">
                      <CheckCircle className="h-5 w-5 text-green-600" />
                      <div>
                        <p className="text-sm font-medium text-green-600 dark:text-green-400">Package Completed</p>
                        <p className="text-xs text-muted-foreground">
                          Completed on {new Date(pkg.completed_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </FocusedLayout>
  );
};